﻿import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";

const getSupportedAudioMimeType = () => {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  const candidates = [
    "audio/ogg;codecs=opus",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4"
  ];

  return candidates.find(type => window.MediaRecorder.isTypeSupported(type)) || "";
};

const getAudioFileExtension = mimeType => {
  if (!mimeType) {
    return "webm";
  }

  if (mimeType.includes("ogg")) {
    return "ogg";
  }

  if (mimeType.includes("mp4")) {
    return "m4a";
  }

  return "webm";
};

const buildQuotedMessagePayload = message => {
  if (!message?.id) {
    return null;
  }

  return {
    id: message.id,
    fromMe: Boolean(message.fromMe),
    body: message.body || "",
    mediaType: message.mediaType || "",
    contact: message.contact
      ? {
          id: message.contact.id,
          name: message.contact.name,
          number: message.contact.number,
          lid: message.contact.lid,
        }
      : undefined,
  };
};

const parseTemplateCompositeKey = value => {
  const [name = "", language = "pt_BR"] = String(value || "").split("__");
  return {
    name: String(name || "").trim(),
    language: String(language || "pt_BR").trim() || "pt_BR",
  };
};

const extractTemplateVariableIndexes = template => {
  const bodyText = String(template?.bodyText || "");
  const matches = [...bodyText.matchAll(/{{\s*(\d+)\s*}}/g)];
  const numbersFromBody = Array.from(
    new Set(
      matches
        .map(item => Number(item?.[1] || 0))
        .filter(value => Number.isInteger(value) && value > 0)
    )
  ).sort((a, b) => a - b);

  if (numbersFromBody.length) {
    return numbersFromBody;
  }

  const fallbackCount = Number(template?.bodyVariablesCount || 0);
  if (!Number.isInteger(fallbackCount) || fallbackCount <= 0) {
    return [];
  }

  return Array.from({ length: fallbackCount }, (_, index) => index + 1);
};

const renderTemplatePreviewText = (template, valuesByIndex = {}) => {
  const bodyText = String(template?.bodyText || "").trim();
  if (!bodyText) {
    return "Template sem texto de corpo.";
  }

  return bodyText.replace(/{{\s*(\d+)\s*}}/g, (_, indexRaw) => {
    const key = Number(indexRaw);
    const replacement = String(valuesByIndex?.[key] || "").trim();
    return replacement || `{{${key}}}`;
  });
};

const getFirstName = fullName => {
  const normalized = String(fullName || "").trim();
  if (!normalized) return "";
  return normalized.split(/\s+/)[0] || normalized;
};

// Espelha o MAX_UPLOAD_FILES do backend (5). Passar disso faz o multer
// recusar o lote inteiro, e o atendente perderia todos os arquivos.
const MAX_DROPPED_FILES = 5;

const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(17, 24, 39, 0.98) 100%)"
        : "#ffffff",
    color: theme.palette.text.primary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.14)"
        : "rgba(148, 163, 184, 0.20)"
    }`,
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.92)"
        : "#ffffff",
    width: "100%",
    display: "flex",
    padding: "9px 12px",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background:
      theme.palette.type === "dark"
        ? "rgba(17, 24, 39, 0.92)"
        : "#f6f8fb",
    display: "flex",
    borderRadius: 14,
    flex: 1,
    position: "relative",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.20)"
        : "rgba(148, 163, 184, 0.28)"
    }`,
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:focus-within": {
      borderColor: "rgba(13, 148, 136, 0.55)",
      boxShadow: "0 0 0 3px rgba(13, 148, 136, 0.12)",
    },
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    color: theme.palette.text.primary,
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1
    }
  },

  sendMessageIcons: {
    color: theme.palette.text.secondary,
  },

  uploadInput: {
    display: "none",
  },

  dropOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontWeight: 700,
    fontSize: "0.95rem",
    pointerEvents: "none",
    color: theme.palette.primary.main,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: 8,
    background:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.92)"
        : "rgba(255, 255, 255, 0.92)",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.94)"
        : "#eee",
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: theme.palette.error.main,
  },

  sendAudioIcon: {
    color: theme.palette.success.main,
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.14)"
        : "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: `1px solid ${theme.palette.divider}`,
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          background: theme.palette.action.hover,
          cursor: "pointer",
        },
      },
    },
  },
  mentionSuggestionsWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    left: 0,
    width: "100%",
    maxHeight: 220,
    overflowY: "auto",
    zIndex: 5,
  },
  mentionSuggestionItem: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  mentionSuggestionItemActive: {
    backgroundColor: theme.palette.action.hover,
  },
  readOnlyNotice: {
    width: "100%",
    padding: "8px 14px 0",
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
  },
  toggleControl: {
    margin: 0,
    marginRight: 8,
    paddingLeft: 10,
    height: 34,
    borderRadius: 999,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.12)"
        : "rgba(148,163,184,0.35)"
    }`,
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#ffffff",
    transition: "border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease",
    "& > span:last-child": {
      fontSize: "0.8rem",
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    "& > span:first-child": {
      marginRight: -4,
    },
  },
  actionRequiredBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "10px 16px",
    background:
      theme.palette.type === "dark" ? "rgba(224,161,6,0.12)" : "#fff8eb",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(224,161,6,0.28)" : "#fde9c3"
    }`,
  },
  acceptNowButton: {
    marginLeft: "auto",
    flexShrink: 0,
    borderRadius: 10,
    padding: "6px 14px",
    fontWeight: 700,
    fontSize: "0.8rem",
    textTransform: "none",
    color: "#ffffff",
    background: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
    boxShadow: "0 6px 14px -4px rgba(13,148,136,0.5)",
    "&:hover": {
      background: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
      filter: "brightness(1.05)",
    },
    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.75)",
      background: "linear-gradient(135deg, #7fd4ca 0%, #5aa79f 100%)",
      boxShadow: "none",
    },
  },
  actionRequiredIcon: {
    color: theme.palette.type === "dark" ? "#fbbf24" : "#e0a106",
    fontSize: "1.3rem",
    flexShrink: 0,
  },
  actionRequiredText: {
    fontSize: "0.82rem",
    lineHeight: 1.35,
    color: theme.palette.type === "dark" ? "#fbbf24" : "#9a6700",
    "& b": {
      fontWeight: 700,
      display: "block",
      color: theme.palette.type === "dark" ? "#fcd34d" : "#7a5200",
    },
  },
  toggleOnTeal: {
    borderColor: "rgba(13,148,136,0.5)",
    background:
      theme.palette.type === "dark" ? "rgba(13,148,136,0.12)" : "#ecfdf5",
    "& > span:last-child": {
      color: theme.palette.type === "dark" ? "#5eead4" : "#0f766e",
    },
  },
  toggleOnAmber: {
    borderColor: "rgba(224,161,6,0.55)",
    background:
      theme.palette.type === "dark" ? "rgba(224,161,6,0.12)" : "#fffaf0",
    "& > span:last-child": {
      color: theme.palette.type === "dark" ? "#fbbf24" : "#9a6700",
    },
  },
  templateInput: {
    width: "100%",
    background: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    padding: "6px 10px",
    color: theme.palette.text.primary,
    "& input": {
      color: theme.palette.text.primary,
    },
    "& input::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  templateHint: {
    color: theme.palette.text.secondary,
    padding: "0 6px",
  },
  templateModeNotice: {
    padding: "8px 10px",
    color: theme.palette.text.secondary,
    width: "100%",
    cursor: "pointer",
  },
  templateModeNoticeTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 4,
  },
  templateInlineHint: {
    width: "100%",
    padding: "0 12px 6px",
    color: theme.palette.text.secondary,
  },
  sendTemplateButton: {
    borderRadius: 18,
    textTransform: "none",
    fontWeight: 600,
    paddingLeft: 14,
    paddingRight: 14,
    minWidth: 146,
  },
  templateSummaryCard: {
    width: "100%",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    background: theme.palette.background.paper,
  },
  templateSummaryText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  templateSummaryValue: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  templateDialogPreview: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(17, 24, 39, 0.6)"
        : "rgba(248, 250, 252, 0.9)",
    marginBottom: 12,
    whiteSpace: "pre-wrap",
  },
  templateVariablesWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  templateVariableRow: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  templateTokenChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
}));

const MessageInput = ({
  ticketStatus,
  canWrite = true,
  canComment = false,
  isGroup = false,
  ticket = null,
  ticketWhatsapp = null,
  wabaTemplateRequired = false,
  onMessagesCreated = () => {},
}) => {
  const classes = useStyles();
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [startingRecording, setStartingRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionState, setMentionState] = useState({
    open: false,
    start: 0,
    end: 0,
    query: "",
    activeIndex: 0,
  });
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [wabaTemplateName, setWabaTemplateName] = useState("");
  const [wabaTemplateLanguage, setWabaTemplateLanguage] = useState("pt_BR");
  const [commentMode, setCommentMode] = useState(false);
  const [useWabaTemplate, setUseWabaTemplate] = useState(false);
  const [wabaTemplatesLoading, setWabaTemplatesLoading] = useState(false);
  const [wabaTemplateOptions, setWabaTemplateOptions] = useState([]);
  const [wabaTemplateKey, setWabaTemplateKey] = useState("");
  const [wabaTemplateVariables, setWabaTemplateVariables] = useState({});
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioMimeTypeRef = useRef("");

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);
  const [acceptingTicket, setAcceptingTicket] = useState(false);

  // Aceita o ticket direto do banner, sem obrigar o atendente a subir até o
  // cabecalho. Durante sessão de suporte não atribui o ticket ao usuário logado.
  const handleAcceptTicket = async () => {
    if (!ticketId) return;
    setAcceptingTicket(true);
    try {
      const operationalUserId = user?.supportSession?.active
        ? undefined
        : user?.id;
      const payload = { status: "open" };
      if (typeof operationalUserId !== "undefined") {
        payload.userId = operationalUserId;
      }
      await api.put(`/tickets/${ticketId}`, payload);
    } catch (err) {
      toastError(err);
    } finally {
      setAcceptingTicket(false);
    }
  };

  // A Gupshup entra junto: e o mesmo WhatsApp oficial, com as mesmas regras de
  // template e de janela de 24h. O nome da variavel ficou historico.
  const isGupshupConnection =
    String(ticketWhatsapp?.connectionType || "").toLowerCase() === "gupshup";
  const isWebchatConnection =
    String(ticketWhatsapp?.connectionType || "").toLowerCase() === "webchat";
  const isWabaConnection =
    isGupshupConnection ||
    String(ticketWhatsapp?.connectionType || "").toLowerCase() === "waba" &&
    Boolean(ticketWhatsapp?.wabaEnabled);
  const isPlatformInitiatedWabaConversation = Boolean(
    ticket?.wabaConversationInitiatedByPlatform
  );
  const templateModeActive = !commentMode && isWabaConnection && useWabaTemplate;
  const canTypeMessage = canWrite || canComment;
  const selectedWabaTemplate = wabaTemplateOptions.find(
    item =>
      String(item.name) === String(wabaTemplateName) &&
      String(item.language) === String(wabaTemplateLanguage)
  );
  const templateNeedsBodyParameters =
    Number(selectedWabaTemplate?.bodyVariablesCount || 0) > 0;
  const selectedTemplateVariableIndexes =
    extractTemplateVariableIndexes(selectedWabaTemplate);
  const templatePreviewText = renderTemplatePreviewText(
    selectedWabaTemplate,
    wabaTemplateVariables
  );
  const templateTokenOptions = [
    {
      label: "Protocolo",
      value: String(ticket?.protocol || "").trim(),
    },
    {
      label: "ID do Ticket",
      value: String(ticket?.id || ticketId || "").trim(),
    },
    {
      label: "Primeiro Nome",
      value: getFirstName(ticket?.contact?.name),
    },
    {
      label: "Nome Completo",
      value: String(ticket?.contact?.name || "").trim(),
    },
    {
      label: "MS (Timestamp)",
      value: String(Date.now()),
    },
    {
      label: "Data",
      value: new Date().toLocaleDateString("pt-BR"),
    },
  ].filter(item => String(item.value || "").trim());

  const getTemplateLabel = template => {
    const category = String(template?.category || "").trim().toUpperCase();
    const name = String(template?.name || "").trim();
    const language = String(template?.language || "").trim();
    return `${category ? `[${category}] ` : ""}${name} (${language})`;
  };

  const applyTemplateFromKey = value => {
    const normalizedValue = String(value || "").trim();
    setWabaTemplateKey(normalizedValue);

    const selected = wabaTemplateOptions.find(
      item => `${item.name}__${item.language}` === normalizedValue
    );

    if (selected) {
      setWabaTemplateName(String(selected.name || "").trim());
      setWabaTemplateLanguage(String(selected.language || "pt_BR").trim() || "pt_BR");
      return;
    }

    const parsed = parseTemplateCompositeKey(normalizedValue);
    setWabaTemplateName(parsed.name);
    setWabaTemplateLanguage(parsed.language);
  };

  const resetTemplateMode = () => {
    setUseWabaTemplate(false);
    setWabaTemplateName("");
    setWabaTemplateLanguage("pt_BR");
    setWabaTemplateKey("");
    setWabaTemplateVariables({});
    setTemplateDialogOpen(false);
  };

  const normalizeMentionText = value =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const releaseRecorderResources = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }

    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    audioMimeTypeRef.current = "";
  };

  const stopRecordingAndGetBlob = () =>
    new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      const mimeType =
        audioMimeTypeRef.current || recorder?.mimeType || "audio/webm";

      if (!recorder) {
        resolve(new Blob([], { type: mimeType }));
        return;
      }

      if (recorder.state === "inactive") {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        releaseRecorderResources();
        resolve(blob);
        return;
      }

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        releaseRecorderResources();
        resolve(blob);
      };

      recorder.onerror = event => {
        const error = event?.error || new Error("Falha ao finalizar a gravação.");
        releaseRecorderResources();
        reject(error);
      };

      recorder.stop();
    });

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setSelectedMentions([]);
      setMentionState({
        open: false,
        start: 0,
        end: 0,
        query: "",
        activeIndex: 0,
      });
      setMentionSuggestions([]);
      setShowEmoji(false);
      setMedias([]);
      setUseWabaTemplate(false);
      setWabaTemplateName("");
      setWabaTemplateLanguage("pt_BR");
      setWabaTemplateOptions([]);
      setWabaTemplateKey("");
      setWabaTemplateVariables({});
      setTemplateDialogOpen(false);
      setReplyingMessage(null);
      releaseRecorderResources();
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    let isMounted = true;

    const loadWabaTemplates = async () => {
      if (!isWabaConnection || !useWabaTemplate || !ticketWhatsapp?.id) {
        if (isMounted) {
          setWabaTemplateOptions([]);
          setWabaTemplatesLoading(false);
          setWabaTemplateKey("");
        }
        return;
      }

      setWabaTemplatesLoading(true);
      try {
        const { data } = await api.get(
          `/whatsapp/${ticketWhatsapp.id}/waba/templates`
        );
        if (!isMounted) return;
        const options = Array.isArray(data?.templates) ? data.templates : [];
        setWabaTemplateOptions(options);
      } catch (err) {
        if (isMounted) {
          setWabaTemplateOptions([]);
        }
      } finally {
        if (isMounted) {
          setWabaTemplatesLoading(false);
        }
      }
    };

    loadWabaTemplates();

    return () => {
      isMounted = false;
    };
  }, [isWabaConnection, useWabaTemplate, ticketWhatsapp?.id]);

  useEffect(() => {
    if (!selectedWabaTemplate) {
      setWabaTemplateVariables({});
      return;
    }

    const indexes = extractTemplateVariableIndexes(selectedWabaTemplate);
    setWabaTemplateVariables(prevState => {
      const nextState = {};
      indexes.forEach(index => {
        nextState[index] = String(prevState?.[index] || "");
      });
      return nextState;
    });
  }, [selectedWabaTemplate?.id, selectedWabaTemplate?.name, selectedWabaTemplate?.language]);

  useEffect(() => {
    if (!isWabaConnection || commentMode || !wabaTemplateRequired) {
      return;
    }

    if (!useWabaTemplate) {
      setUseWabaTemplate(true);
    }
  }, [isWabaConnection, commentMode, wabaTemplateRequired, useWabaTemplate]);

  useEffect(() => {
    if (!canComment && commentMode) {
      setCommentMode(false);
    }
    if (canComment && !canWrite && !commentMode) {
      setCommentMode(true);
    }
  }, [canComment, canWrite, commentMode]);

  useEffect(() => () => {
    releaseRecorderResources();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadMentionCandidates = async () => {
      if (!isGroup || !ticketId) {
        if (isMounted) {
          setMentionCandidates([]);
        }
        return;
      }

      try {
        const { data } = await api.get(`/messages/${ticketId}/mentions`);
        if (!isMounted) return;

        const mentions = Array.isArray(data?.mentions) ? data.mentions : [];
        setMentionCandidates(mentions);
      } catch (err) {
        if (isMounted) {
          setMentionCandidates([]);
        }
      }
    };

    loadMentionCandidates();

    return () => {
      isMounted = false;
    };
  }, [ticketId, isGroup]);

  const closeMentionSuggestions = () => {
    setMentionState({
      open: false,
      start: 0,
      end: 0,
      query: "",
      activeIndex: 0,
    });
    setMentionSuggestions([]);
  };

  const getMentionSuggestions = query => {
    const normalizedQuery = normalizeMentionText(query);
    const candidates = mentionCandidates.filter(candidate => {
      const name = normalizeMentionText(candidate.name);
      const number = String(candidate.number || "").replace(/\D/g, "");
      if (!normalizedQuery) return true;
      return name.includes(normalizedQuery) || number.startsWith(normalizedQuery);
    });

    return candidates.slice(0, 8);
  };

  const detectMentionAtCursor = (value, cursorPosition) => {
    if (!isGroup || !canWrite) {
      closeMentionSuggestions();
      return;
    }

    const safeCursor =
      typeof cursorPosition === "number" ? cursorPosition : value.length;
    const beforeCursor = value.slice(0, safeCursor);
    const match = beforeCursor.match(/(?:^|[\s\n])@([^\s@]{0,40})$/);

    if (!match) {
      closeMentionSuggestions();
      return;
    }

    const query = match[1] || "";
    const suggestions = getMentionSuggestions(query);

    if (!suggestions.length) {
      closeMentionSuggestions();
      return;
    }

    setMentionSuggestions(suggestions);
    setMentionState({
      open: true,
      query,
      start: safeCursor - query.length - 1,
      end: safeCursor,
      activeIndex: 0,
    });
  };

  const applyMentionSuggestion = candidate => {
    if (!candidate || !mentionState.open) return;

    const before = inputMessage.slice(0, mentionState.start);
    const after = inputMessage.slice(mentionState.end);
    // O WhatsApp so renderiza mencao quando o texto traz "@<numero>" e esse
    // numero casa com um dos mentionedJid. Inserir "@Nome" chega como texto
    // puro no destinatario, mesmo com o mentionedJid correto. O token vem do
    // proprio jid (e nao do campo number) para funcionar tambem em contato
    // identificado por LID, onde os dois diferem.
    const mentionToken =
      String(candidate.jid || "").split("@")[0].replace(/\D/g, "") ||
      String(candidate.number || "").replace(/\D/g, "");
    const mentionLabel = mentionToken ? `@${mentionToken}` : `@${candidate.name}`;
    const nextMessage = `${before}${mentionLabel} ${after}`;

    setInputMessage(nextMessage);
    setSelectedMentions(prevState => {
      const normalizedJid = String(candidate.jid || "").trim();
      if (!normalizedJid) return prevState;

      const alreadyIncluded = prevState.some(
        item => String(item.jid) === normalizedJid
      );

      if (alreadyIncluded) {
        return prevState;
      }

      return [
        ...prevState,
        {
          jid: normalizedJid,
          name: String(candidate.name || "").trim(),
          number: String(candidate.number || "").trim(),
        },
      ];
    });

    closeMentionSuggestions();

    const nextCursorPosition = `${before}${mentionLabel} `.length;
    setTimeout(() => {
      if (inputRef.current?.focus) {
        inputRef.current.focus();
      }
      if (inputRef.current?.setSelectionRange) {
        inputRef.current.setSelectionRange(nextCursorPosition, nextCursorPosition);
      }
    }, 0);
  };

  // "Digitando" para o visitante do webchat. No maximo um aviso a cada 3s -
  // o backend segura o estado por 6s, entao a sequencia de avisos cobre a
  // digitacao inteira sem uma requisicao por tecla. Comentario interno nao
  // avisa: o visitante nunca vai ver aquele texto.
  const lastTypingNoticeRef = useRef(0);
  const notifyWebchatTyping = value => {
    if (!isWebchatConnection || commentMode || !ticketId) return;
    if (!String(value || "").trim()) return;
    const now = Date.now();
    if (now - lastTypingNoticeRef.current < 3000) return;
    lastTypingNoticeRef.current = now;
    api.post(`/messages/${ticketId}/typing`).catch(() => {});
  };

  const handleChangeInput = e => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setInputMessage(value);
    handleLoadQuickAnswer(value);
    detectMentionAtCursor(value, cursorPosition);
    notifyWebchatTyping(value);
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setTypeBar(false);
  };

  // Resposta rapida com anexo e enviada pelo backend, que já tem o arquivo:
  // baixar e reenviar pelo navegador gastaria a transferencia duas vezes e
  // esbarraria em CORS quando a midia esta no S3. O texto vira legenda.
  const handleQuickAnswerWithMediaClick = async quickAnswer => {
    setTypeBar(false);
    setLoading(true);

    try {
      await api.post(`/quickAnswers/${quickAnswer.id}/send`, { ticketId });
      setInputMessage("");
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  // Contador em vez de booleano: dragleave dispara ao passar por CADA filho, e
  // com booleano o aviso piscava enquanto o arquivo era arrastado por cima.
  const dragDepthRef = useRef(0);

  // O WebChat ainda nao mostra nem recebe arquivo (o backend recusa): anexo,
  // audio, colar imagem e arrastar ficam desligados nesses chamados.
  const canReceiveDroppedFiles =
    !isWebchatConnection &&
    ticketStatus === "open" &&
    canWrite &&
    !loading &&
    !recording &&
    !startingRecording &&
    !templateModeActive &&
    !commentMode;

  const handleDragEnter = e => {
    if (!canReceiveDroppedFiles) return;
    // Arrastar texto de dentro da propria conversa tambem dispara drag: sem
    // esta checagem o aviso de anexo aparecia ao selecionar e mover texto.
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingFile(true);
  };

  const handleDragOver = e => {
    if (!canReceiveDroppedFiles) return;
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    // Sem preventDefault no dragover o navegador ABRE o arquivo numa aba nova
    // em vez de entregar o drop.
    e.preventDefault();
  };

  const handleDragLeave = e => {
    if (!canReceiveDroppedFiles) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFile(false);
    }
  };

  const handleDropMedias = e => {
    dragDepthRef.current = 0;
    setIsDraggingFile(false);

    if (!canReceiveDroppedFiles) return;

    const droppedFiles = Array.from(e.dataTransfer?.files || []);
    if (!droppedFiles.length) return;

    e.preventDefault();
    // MAX_UPLOAD_FILES do backend e 5; passar disso derruba o envio inteiro,
    // entao corta aqui e avisa em vez de deixar falhar depois de subir tudo.
    if (droppedFiles.length > MAX_DROPPED_FILES) {
      toast.info(i18n.t("messagesInput.dropTooManyFiles"));
    }
    setMedias(droppedFiles.slice(0, MAX_DROPPED_FILES));
  };

  const handleInputPaste = e => {
    if (isWebchatConnection) return;
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      const { data } = await api.post(`/messages/${ticketId}`, formData);
      onMessagesCreated(data?.messages || []);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleTemplateVariableChange = (index, value) => {
    setWabaTemplateVariables(prevState => ({
      ...prevState,
      [index]: value,
    }));
  };

  const handleApplyTemplateToken = (index, tokenValue) => {
    const nextValue = String(tokenValue || "").trim();
    if (!nextValue) return;
    handleTemplateVariableChange(index, nextValue);
  };

  const openTemplateDialog = () => {
    if (!useWabaTemplate) {
      setUseWabaTemplate(true);
    }
    setTemplateDialogOpen(true);
  };

  const closeTemplateDialog = () => {
    setTemplateDialogOpen(false);
  };

  const handleConfirmTemplateDialog = () => {
    if (!String(wabaTemplateName || "").trim()) {
      toast.warn("Selecione um template aprovado para continuar.");
      return;
    }

    if (
      selectedTemplateVariableIndexes.length &&
      selectedTemplateVariableIndexes.some(
        index => !String(wabaTemplateVariables?.[index] || "").trim()
      )
    ) {
      toast.warn("Preencha todas as variáveis do template.");
      return;
    }

    setTemplateDialogOpen(false);
  };

  const handleSendMessage = async () => {
    if (commentMode) {
      if (inputMessage.trim() === "") return;
      setLoading(true);
      closeMentionSuggestions();

      const parsedBody = signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim();

    try {
      const { data } = await api.post(`/messages/${ticketId}/comment`, { body: parsedBody });
      if (data?.message) {
        onMessagesCreated([data.message]);
      }
    } catch (err) {
      toastError(err);
    }

      setInputMessage("");
      setSelectedMentions([]);
      setShowEmoji(false);
      setLoading(false);
      setReplyingMessage(null);
      return;
    }

    const hasTemplateData =
      isWabaConnection &&
      useWabaTemplate &&
      Boolean(String(wabaTemplateName || "").trim());
    if (inputMessage.trim() === "" && !(isWabaConnection && hasTemplateData)) return;
    setLoading(true);
    closeMentionSuggestions();

    const parsedBody = signMessage
      ? `*${user?.name}:*\n${inputMessage.trim()}`
      : inputMessage.trim();
    const normalizedBody = normalizeMentionText(parsedBody);
    const mentions = selectedMentions
      .filter(item => {
        const mentionName = normalizeMentionText(item?.name);
        const mentionNumber = String(item?.number || "").replace(/\D/g, "");
        // O token inserido no texto vem do jid, que em contato por LID difere
        // do campo number - sem olhar para ele aqui, a mencao seria descartada
        // justamente nesses casos.
        const mentionJidUser = String(item?.jid || "")
          .split("@")[0]
          .replace(/\D/g, "");

        const hasMentionByName =
          mentionName && normalizedBody.includes(`@${mentionName}`);
        const hasMentionByNumber =
          mentionNumber && normalizedBody.includes(`@${mentionNumber}`);
        const hasMentionByJid =
          mentionJidUser && normalizedBody.includes(`@${mentionJidUser}`);

        return hasMentionByName || hasMentionByNumber || hasMentionByJid;
      })
      .map(item => item.jid)
      .filter(Boolean);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: parsedBody,
      quotedMsg: buildQuotedMessagePayload(replyingMessage),
      mentions,
    };

    if (hasTemplateData) {
      const bodyParameters = templateNeedsBodyParameters
        ? selectedTemplateVariableIndexes.map(index =>
            String(wabaTemplateVariables?.[index] || "").trim()
          )
        : [];

      if (
        templateNeedsBodyParameters &&
        bodyParameters.some(item => !String(item || "").trim())
      ) {
        setLoading(false);
        toast.warn("Preencha todas as variáveis do template antes de enviar.");
        return;
      }

      const normalizedTemplateName = String(wabaTemplateName || "").trim();
      const normalizedTemplateLanguage =
        String(wabaTemplateLanguage || "").trim() || "pt_BR";
      const signedBy = String(user?.name || "").trim();
      const signedLine = signedBy ? `*${signedBy}:*` : "";
      // O nome do template nao vai mais no corpo: agora e coluna em Messages e
      // aparece no proprio marcador do balao. No corpo ele ainda passava pelo
      // renderizador de markdown, que come os "_" do nome e mostrava
      // "iniciar conversa api" em italico no lugar de "iniciar_conversa_api".
      const composedPreviewBody = [
        signedLine,
        String(templatePreviewText || "").trim(),
      ]
        .filter(Boolean)
        .join("\n");

      message.wabaTemplate = {
        name: normalizedTemplateName,
        languageCode: normalizedTemplateLanguage,
        bodyParameters,
        previewBody: composedPreviewBody,
      };
    }

    try {
      const { data } = await api.post(`/messages/${ticketId}`, message);
      onMessagesCreated(data?.messages || []);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setSelectedMentions([]);
    setTemplateDialogOpen(false);
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    setStartingRecording(true);
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices?.getUserMedia ||
        typeof window === "undefined" ||
        typeof window.MediaRecorder === "undefined"
      ) {
        throw new Error("Gravação de áudio não suportada neste navegador.");
      }

      releaseRecorderResources();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      const recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream);

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      audioMimeTypeRef.current = recorder.mimeType || mimeType || "audio/webm";

      recorder.onstart = () => {
        setRecording(true);
        setStartingRecording(false);
        setLoading(false);
      };

      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        releaseRecorderResources();
        setRecording(false);
        setStartingRecording(false);
        setLoading(false);
      };

      recorder.start();
    } catch (err) {
      toastError(err);
      setStartingRecording(false);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: value.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const blob = await stopRecordingAndGetBlob();
      if (blob.size < 1000) {
        setLoading(false);
        setRecording(false);
        setStartingRecording(false);
        return;
      }

      const formData = new FormData();
      const extension = getAudioFileExtension(blob.type);
      const filename = `${new Date().getTime()}.${extension}`;
      formData.append("medias", blob, filename);
      formData.append("fromMe", true);

      const { data } = await api.post(`/messages/${ticketId}`, formData);
      onMessagesCreated(data?.messages || []);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setStartingRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        await stopRecordingAndGetBlob();
      } else {
        releaseRecorderResources();
      }
      setRecording(false);
      setStartingRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleInputKeyDown = e => {
    if (mentionState.open && mentionSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionState(prevState => ({
          ...prevState,
          activeIndex: (prevState.activeIndex + 1) % mentionSuggestions.length,
        }));
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionState(prevState => ({
          ...prevState,
          activeIndex:
            (prevState.activeIndex - 1 + mentionSuggestions.length) %
            mentionSuggestions.length,
        }));
        return;
      }

      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const candidate = mentionSuggestions[mentionState.activeIndex];
        applyMentionSuggestion(candidate);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        closeMentionSuggestions();
        return;
      }
    }

    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      !loading &&
      canTypeMessage
    ) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMenuItemClick = event => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  if (medias.length > 0)
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <IconButton
          aria-label="cancel-upload"
          component="span"
          onClick={e => setMedias([])}
        >
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>

        {loading ? (
          <div>
            <CircularProgress className={classes.circleLoading} />
          </div>
        ) : (
          <span>
            {medias[0]?.name}
            {/* <img src={media.preview} alt=""></img> */}
          </span>
        )}
        <IconButton
          aria-label="send-upload"
          component="span"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          <SendIcon className={classes.sendMessageIcons} />
        </IconButton>
      </Paper>
    );
  else {
    return (
      <Paper
        square
        elevation={0}
        className={classes.mainWrapper}
        style={{ position: "relative" }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDropMedias}
      >
        {isDraggingFile && (
          <div className={classes.dropOverlay}>
            <AttachFileIcon fontSize="small" />
            {i18n.t("messagesInput.dropHint")}
          </div>
        )}
        {ticketStatus === "pending" && (
          <div className={classes.actionRequiredBanner}>
            <ReportProblemOutlinedIcon className={classes.actionRequiredIcon} />
            <span className={classes.actionRequiredText}>
              <b>Ação necessária</b>
              Aceite o ticket para liberar o envio de mensagens.
            </span>
            <Button
              size="small"
              variant="contained"
              className={classes.acceptNowButton}
              startIcon={<DoneAllIcon />}
              disabled={acceptingTicket}
              onClick={handleAcceptTicket}
            >
              {acceptingTicket ? "Aceitando..." : "Aceitar agora"}
            </Button>
          </div>
        )}
        {!canWrite && !canComment && ticketStatus === "open" && (
          <Typography className={classes.readOnlyNotice}>
            Este chamado pertence a outro atendente. Você pode visualizar, mas não enviar mensagens.
          </Typography>
        )}
        {!canWrite && canComment && ticketStatus === "open" && (
          <Typography className={classes.readOnlyNotice}>
            Este chamado pertence a outro atendente. Você pode inserir apenas comentario interno.
          </Typography>
        )}
        {canComment && (
          <Hidden only={["md", "lg", "xl"]}>
            <div
              style={{
                width: "100%",
                padding: "6px 12px 0",
                display: "flex",
              }}
            >
              <FormControlLabel
                className={`${classes.toggleControl} ${
                  commentMode ? classes.toggleOnAmber : ""
                }`.trim()}
                label="Comentario interno"
                control={
                  <Switch
                    size="small"
                    checked={commentMode}
                    onChange={e => setCommentMode(e.target.checked)}
                    color="primary"
                    disabled={loading || ticketStatus !== "open"}
                  />
                }
              />
            </div>
          </Hidden>
        )}
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        {isWabaConnection && !commentMode && (
          <div
            style={{
              width: "100%",
              padding: "6px 12px 0",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <FormControlLabel
              className={classes.toggleControl}
              label="Usar template WABA"
              control={
                <Switch
                  size="small"
                  checked={useWabaTemplate}
                  onChange={e => {
                    const nextValue = e.target.checked;
                    if (!nextValue && wabaTemplateRequired) {
                      return;
                    }
                    if (!nextValue) {
                      resetTemplateMode();
                      return;
                    }
                    setUseWabaTemplate(true);
                  }}
                  color="primary"
                  disabled={
                    !canWrite ||
                    loading ||
                    ticketStatus !== "open" ||
                    wabaTemplateRequired
                  }
                />
              }
            />
            {useWabaTemplate && (
              <>
                <div className={classes.templateSummaryCard}>
                  <div className={classes.templateSummaryText}>
                    <Typography variant="caption" className={classes.templateHint}>
                      {wabaTemplateRequired
                        ? isPlatformInitiatedWabaConversation
                          ? "Janela de 24h expirada: envio por template obrigatório."
                          : "Janela de 24h indisponivel: envio por template obrigatório."
                        : "Template WABA selecionado"}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.templateSummaryValue}
                    >
                      {String(wabaTemplateName || "").trim()
                        ? `${wabaTemplateName} (${wabaTemplateLanguage})`
                        : "Nenhum template configurado"}
                    </Typography>
                    {selectedWabaTemplate && (
                      <Typography variant="caption" className={classes.templateHint}>
                        Tipo: {String(selectedWabaTemplate.category || "-").toUpperCase()}{" "}
                        {selectedTemplateVariableIndexes.length
                          ? `| Variáveis: ${selectedTemplateVariableIndexes.length}`
                          : "| Sem variáveis"}
                      </Typography>
                    )}
                  </div>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={openTemplateDialog}
                    disabled={!canWrite || loading || ticketStatus !== "open"}
                  >
                    Configurar
                  </Button>
                </div>
                <Typography variant="caption" className={classes.templateInlineHint}>
                  Clique em <strong>Configurar</strong> para preencher as variáveis e depois em <strong>Enviar template</strong>.
                </Typography>
              </>
            )}
          </div>
        )}
        <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canTypeMessage || templateModeActive || commentMode}
              onClick={e => setShowEmoji(prevState => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={e => setShowEmoji(false)}>
                  <Picker
                    perLine={16}
                    showPreview={false}
                    showSkinTones={false}
                    onSelect={handleAddEmoji}
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode || isWebchatConnection}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                  aria-label="upload"
                  component="span"
                  disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode || isWebchatConnection}
                >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            {canComment && (
              <FormControlLabel
                className={`${classes.toggleControl} ${
                  commentMode ? classes.toggleOnAmber : ""
                }`.trim()}
                label="Comentario interno"
                control={
                  <Switch
                    size="small"
                    checked={commentMode}
                    onChange={e => setCommentMode(e.target.checked)}
                    color="primary"
                    disabled={loading || ticketStatus !== "open"}
                  />
                }
              />
            )}
            <FormControlLabel
              className={`${classes.toggleControl} ${
                signMessage ? classes.toggleOnTeal : ""
              }`.trim()}
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={e => {
                    setSignMessage(e.target.checked);
                  }}
                  name="showAllTickets"
                  color="primary"
                  disabled={!canTypeMessage || templateModeActive}
                />
              }
            />
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              disabled={!canTypeMessage}
              onClick={handleOpenMenuClick}
            >
              <MoreVert></MoreVert>
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
            >
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canTypeMessage || templateModeActive || commentMode}
                  onClick={e => setShowEmoji(prevState => !prevState)}
                >
                  <MoodIcon className={classes.sendMessageIcons} />
                </IconButton>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode || isWebchatConnection}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button">
                  <IconButton
                    aria-label="upload"
                    component="span"
                    disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode || isWebchatConnection}
                  >
                    <AttachFileIcon className={classes.sendMessageIcons} />
                  </IconButton>
                </label>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <FormControlLabel
                  className={classes.toggleControl}
                  label={i18n.t("messagesInput.signMessage")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={signMessage}
                      onChange={e => {
                        setSignMessage(e.target.checked);
                      }}
                      name="showAllTickets"
                      color="primary"
                      disabled={!canTypeMessage || templateModeActive}
                    />
                  }
                />
              </MenuItem>
            </Menu>
          </Hidden>
          <div className={classes.messageInputWrapper}>
            {templateModeActive ? (
              <Typography
                variant="body2"
                className={classes.templateModeNotice}
                onClick={openTemplateDialog}
              >
                <span className={classes.templateModeNoticeTitle}>
                  Modo template WABA ativo.
                </span>
                {String(wabaTemplateName || "").trim()
                  ? "Clique aqui para revisar/editar o template e depois use o botao Enviar template."
                  : "Clique aqui para configurar o template e enviar."}
              </Typography>
            ) : (
              <>
                <InputBase
                  inputRef={input => {
                    input && input.focus();
                    input && (inputRef.current = input);
                  }}
                  className={classes.messageInput}
                  placeholder={
                    commentMode
                      ? "Digite um comentario interno..."
                      : ticketStatus === "open"
                      ? i18n.t("messagesInput.placeholderOpen")
                      : i18n.t("messagesInput.placeholderClosed")
                  }
                  multiline
                  maxRows={5}
                  value={inputMessage}
                  onChange={handleChangeInput}
                  disabled={recording || startingRecording || loading || ticketStatus !== "open"}
                  readOnly={!canTypeMessage}
                  onPaste={e => {
                    ticketStatus === "open" && canWrite && !commentMode && handleInputPaste(e);
                  }}
                  onClick={e => detectMentionAtCursor(inputMessage, e.target.selectionStart)}
                  onKeyDown={handleInputKeyDown}
                />
                {mentionState.open && mentionSuggestions.length > 0 ? (
                  <ClickAwayListener onClickAway={closeMentionSuggestions}>
                    <List className={classes.mentionSuggestionsWrapper}>
                      {mentionSuggestions.map((candidate, index) => (
                        <ListItem
                          button
                          dense
                          key={`${candidate.jid}-${candidate.contactId}`}
                          onClick={() => applyMentionSuggestion(candidate)}
                          className={clsx(classes.mentionSuggestionItem, {
                            [classes.mentionSuggestionItemActive]:
                              index === mentionState.activeIndex,
                          })}
                        >
                          <ListItemText
                            primary={candidate.name}
                            secondary={candidate.number || candidate.jid}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </ClickAwayListener>
                ) : typeBar ? (
                  <ul className={classes.messageQuickAnswersWrapper}>
                    {quickAnswers.map((value, index) => {
                      return (
                        <li
                          className={classes.messageQuickAnswersWrapperItem}
                          key={index}
                        >
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <a
                            onClick={() =>
                              value.mediaUrl
                                ? handleQuickAnswerWithMediaClick(value)
                                : handleQuickAnswersClick(value.message)
                            }
                          >
                            {value.mediaUrl ? "📎 " : ""}
                            {`${value.shortcut} - ${value.message}`}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div></div>
                )}
              </>
            )}
          </div>
          {templateModeActive ? (
            <Button
              aria-label="sendTemplateMessage"
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              className={classes.sendTemplateButton}
              onClick={handleSendMessage}
              disabled={
                loading ||
                !canTypeMessage ||
                !String(wabaTemplateName || "").trim() ||
                selectedTemplateVariableIndexes.some(
                  index => !String(wabaTemplateVariables?.[index] || "").trim()
                )
              }
            >
              Enviar template
            </Button>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <>
              {!inputMessage && (
                <IconButton
                  aria-label="showRecorder"
                  component="span"
                  disabled={loading || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode || isWebchatConnection}
                  onClick={handleStartRecording}
                >
                  {startingRecording ? (
                    <CircularProgress size={22} className={classes.audioLoading} />
                  ) : (
                    <MicIcon className={classes.sendMessageIcons} />
                  )}
                </IconButton>
              )}
              <IconButton
                aria-label="sendMessage"
                component="span"
                onClick={handleSendMessage}
                disabled={loading || !canTypeMessage || !inputMessage}
              >
                <SendIcon className={classes.sendMessageIcons} />
              </IconButton>
            </>
          )}
        </div>
        <Dialog
          open={templateDialogOpen}
          onClose={closeTemplateDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Configurar Template WABA</DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              margin="dense"
              label="Template (nome__idioma)"
              placeholder={
                wabaTemplatesLoading
                  ? "Carregando templates..."
                  : "Ex: envio_boleto_sms__pt_BR"
              }
              value={wabaTemplateKey}
              onChange={e => applyTemplateFromKey(e.target.value)}
              inputProps={{ list: "waba-template-options-dialog" }}
            />
            <datalist id="waba-template-options-dialog">
              {wabaTemplateOptions.map(item => (
                <option
                  key={`${item.name}__${item.language}`}
                  value={`${item.name}__${item.language}`}
                >
                  {getTemplateLabel(item)}
                </option>
              ))}
            </datalist>

            {selectedWabaTemplate ? (
              <>
                <Typography variant="caption" className={classes.templateHint}>
                  Tipo: {String(selectedWabaTemplate.category || "-").toUpperCase()} | Idioma:{" "}
                  {String(selectedWabaTemplate.language || "pt_BR")}
                </Typography>
                <div className={classes.templateDialogPreview}>
                  {templatePreviewText}
                </div>
                {selectedTemplateVariableIndexes.length > 0 ? (
                  <div className={classes.templateVariablesWrapper}>
                    {selectedTemplateVariableIndexes.map(index => (
                      <div key={`template-variable-${index}`} className={classes.templateVariableRow}>
                        <Typography variant="subtitle2">
                          Variável {`{{${index}}}`}
                        </Typography>
                        <div className={classes.templateTokenChips}>
                          {templateTokenOptions.map(token => (
                            <Chip
                              key={`template-token-${index}-${token.label}`}
                              label={token.label}
                              size="small"
                              onClick={() =>
                                handleApplyTemplateToken(index, token.value)
                              }
                              clickable
                            />
                          ))}
                        </div>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          label={`Valor para {{${index}}}`}
                          value={String(wabaTemplateVariables?.[index] || "")}
                          onChange={e =>
                            handleTemplateVariableChange(index, e.target.value)
                          }
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <Typography variant="body2" className={classes.templateHint}>
                    Este template não possui variáveis no corpo.
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" className={classes.templateHint}>
                Selecione um template para visualizar e preencher as variáveis.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTemplateDialog}>Cancelar</Button>
            <Button color="primary" variant="contained" onClick={handleConfirmTemplateDialog}>
              Aplicar template
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    );
  }
};

export default MessageInput;
